import type { ReactNode } from 'react';
import { errorMessage } from '../api/client';

/*
 * Loading, empty and error states, designed rather than improvised. Pages
 * read useQuery's isPending / isError / refetch directly and keep the header,
 * the navigation and every unaffected section on screen while one section
 * loads or fails. PageLoader stays for the route guards only.
 */

/** A grey block standing in for content that is on its way. */
export function Skeleton({
  kind = 'block',
  width,
  height,
  className,
}: {
  kind?: 'block' | 'line';
  width?: string;
  height?: string;
  className?: string;
}) {
  return (
    <span
      className={['skeleton', `skeleton--${kind}`, className].filter(Boolean).join(' ')}
      style={{ width, height }}
      aria-hidden="true"
    />
  );
}

/** A table's worth of skeleton lines, announced once as "Loading". */
export function SkeletonTable({
  rows = 4,
  cols = 4,
  label = 'Loading',
}: {
  rows?: number;
  cols?: number;
  label?: string;
}) {
  return (
    <div className="skeleton-table" role="status" aria-label={label}>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="skeleton-table__row">
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} kind="line" width={c === 0 ? '32%' : `${14 + ((r + c) % 3) * 6}%`} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** A stat row's worth of skeleton tiles. */
export function SkeletonStats({ count = 4 }: { count?: number }) {
  return (
    <div className="stats" role="status" aria-label="Loading">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="stat">
          <Skeleton kind="block" width="3.5rem" height="1.8rem" />
          <Skeleton kind="line" width="60%" />
        </div>
      ))}
    </div>
  );
}

/**
 * One section's failure: the message from the API and a way to try again,
 * wired to the query's refetch. Sits where the content would have been.
 */
export function ErrorState({
  error,
  onRetry,
  retrying = false,
  title = 'Could not load this',
}: {
  error: unknown;
  onRetry?: () => void;
  retrying?: boolean;
  title?: string;
}) {
  return (
    <div className="error-state" role="alert">
      <div className="error-state__text">
        <p className="error-state__title">{title}</p>
        <p className="error-state__body">{errorMessage(error)}</p>
      </div>
      {onRetry ? (
        // A plain button rather than <Button>: ui.tsx re-exports EmptyState from here.
        <button
          type="button"
          className="btn btn--secondary btn--small"
          disabled={retrying}
          aria-busy={retrying || undefined}
          onClick={onRetry}
        >
          {retrying ? 'Trying…' : 'Try again'}
        </button>
      ) : null}
    </div>
  );
}

/**
 * What an empty list is for and the one action that fills it. `body` is one
 * sentence; `action` is already permission-gated by the caller.
 */
export function EmptyState({
  title,
  body,
  action,
  children,
}: {
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  /** Older call sites pass the body as children. */
  children?: ReactNode;
}) {
  const text = body ?? children;
  return (
    <div className="empty">
      <p className="empty__title">{title}</p>
      {text ? <div className="empty__body">{text}</div> : null}
      {action ? <div className="empty__action">{action}</div> : null}
    </div>
  );
}
